import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Box,
  Snackbar,
  Alert,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  Close,
  Search,
} from '@mui/icons-material';
import httpClient from '../../api/axios';

const UserMaster = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [formStatus, setFormStatus] = useState('Update User');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    address: '',
    role: 'user',
    profileImg: '',
  });
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch users when component mounts
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch all users from API
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await httpClient.get('/api/users');
      setUsers(response.data.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Validate the field as user types
    validateField(name, value);
  };

  const handleBlur = (e) => {
    const { name } = e.target;

    // Mark field as touched
    setTouched({ ...touched, [name]: true });

    // Validate the field
    validateField(name, formData[name]);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };

    switch (name) {
      case 'name':
        if (!value.trim()) {
          newErrors.name = 'Name is required';
        } else if (value.trim().length < 3) {
          newErrors.name = 'Name must be at least 3 characters';
        } else {
          delete newErrors.name;
        }
        break;

      case 'email':
        if (!value.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = 'Enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;

      case 'phone':
        if (!value.trim()) {
          newErrors.phone = 'Phone number is required';
        } else if (!/^\d{10}$/.test(value.replace(/\D/g, ''))) {
          newErrors.phone = 'Enter a valid 10-digit phone number';
        } else {
          delete newErrors.phone;
        }
        break;

      case 'password':
        if (!value) {
          newErrors.password = 'Password is required';
        } else if (value.length < 6) {
          newErrors.password =
            'Password must be at least 6 characters';
        } else {
          delete newErrors.password;
        }
        break;

      case 'role':
        if (!value) {
          newErrors.role = 'Role is required';
        } else {
          delete newErrors.role;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleEdit = (user) => {
    setFormStatus('Update User');
    setFormData({
      ...user,
      // If the API sends _id instead of id
      id: user._id || user.id,
    });
    setErrors({});
    setTouched({});
    setOpen(true);
  };

  const handleView = (user) => setViewUser(user);

  const handleDelete = (user) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const userId = userToDelete._id || userToDelete.id;
      await httpClient.delete(`/api/users/${userId}`);

      // Update local state
      setUsers(
        users?.filter((u) => u._id !== userId && u.id !== userId)
      );
      showSnackbar('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      showSnackbar('Failed to delete user', 'error');
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const validateForm = () => {
    // Validate all fields at once
    let err = {};
    if (!formData.name) err.name = 'Name is required';
    if (!formData.email) err.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      err.email = 'Enter a valid email address';
    if (!formData.phone) err.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, '')))
      err.phone = 'Enter a valid 10-digit phone number';
    if (!formData.password) err.password = 'Password is required';
    else if (formData.password.length < 6)
      err.password = 'Password must be at least 6 characters';
    if (!formData.role) err.role = 'Role is required';

    // Mark all fields as touched
    const touchedFields = {};
    Object.keys(formData).forEach((key) => {
      touchedFields[key] = true;
    });
    setTouched(touchedFields);

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      // Extract _id or id for the update request
      const { _id, id, ...userData } = formData;
      const userId = _id || id;

      // For update, send PUT request

      let obj = {
        _id: formData?._id,
        name: formData?.name,
        email: formData?.email,
        phone: formData?.phone,
        password: formData?.password,
        address: formData?.address,
        role: formData?.role,
      };
      const response = await httpClient.put(
        `/api/users/${_id}/profile`,
        obj
      );

      fetchUsers();

      // // Update local state
      // const updated = users.map((u) =>
      //   u._id === userId || u.id === userId ? response.data : u
      // );
      // setUsers(updated);
      showSnackbar('User updated successfully');

      // Close the dialog
      setOpen(false);
    } catch (error) {
      console.error('Error updating user:', error);
      showSnackbar('Failed to update user', 'error');
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, profileImg: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredUsers = users?.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone?.includes(searchQuery) ||
      user.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Determine which columns to show based on screen size
  const getVisibleColumns = () => {
    if (isMobile) {
      return ['image', 'name', 'actions'];
    } else {
      return [
        'image',
        'name',
        'email',
        'phone',
        'role',
        'createdAt',
        'actions',
      ];
    }
  };

  const visibleColumns = getVisibleColumns();

  return (
    <div className='flex justify-center items-start min-h-screen bg-gray-100 p-4 w-full'>
      <Paper className='w-full max-w-7xl p-2 md:p-6 shadow-lg'>
        <Typography
          variant='h5'
          className='text-center font-bold mb-4'
        >
          User Master
        </Typography>

        {/* Search Box */}
        <Box display='flex' gap={2} mb={3}>
          <TextField
            label='Search Users'
            variant='outlined'
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <Search />
                </IconButton>
              ),
            }}
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>

        <TableContainer>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                {visibleColumns.includes('image') && (
                  <TableCell>
                    <strong>Image</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('name') && (
                  <TableCell>
                    <strong>Name</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('email') && (
                  <TableCell>
                    <strong>Email</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('phone') && (
                  <TableCell>
                    <strong>Phone</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('role') && (
                  <TableCell>
                    <strong>Role</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('createdAt') && (
                  <TableCell>
                    <strong>Created At</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('actions') && (
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    align='center'
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredUsers?.length > 0 ? (
                filteredUsers
                  .slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  .map((u) => (
                    <TableRow key={u._id || u.id || u.email}>
                      {visibleColumns.includes('image') && (
                        <TableCell>
                          <Avatar
                            src={
                              u.profileImg || '/api/placeholder/40/40'
                            }
                            alt={u.name}
                            sx={{ width: 40, height: 40 }}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.includes('name') && (
                        <TableCell>{u.name}</TableCell>
                      )}
                      {visibleColumns.includes('email') && (
                        <TableCell>{u.email}</TableCell>
                      )}
                      {visibleColumns.includes('phone') && (
                        <TableCell>{u.phone}</TableCell>
                      )}
                      {visibleColumns.includes('role') && (
                        <TableCell>{u.role}</TableCell>
                      )}
                      {visibleColumns.includes('createdAt') && (
                        <TableCell>
                          {new Date(u.createdAt).toLocaleDateString()}
                        </TableCell>
                      )}
                      {visibleColumns.includes('actions') && (
                        <TableCell>
                          <Box
                            display='flex'
                            flexDirection={
                              isMobile ? 'column' : 'row'
                            }
                            gap={1}
                          >
                            <IconButton
                              onClick={() => handleView(u)}
                              className='!bg-amber-500'
                              size='small'
                            >
                              <Visibility fontSize='small' />
                            </IconButton>
                            <IconButton
                              onClick={() => handleEdit(u)}
                              className='!bg-amber-500'
                              size='small'
                            >
                              <Edit fontSize='small' />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDelete(u)}
                              className='!bg-amber-500'
                              size='small'
                            >
                              <Delete fontSize='small' />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    align='center'
                  >
                    No Users Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component='div'
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      </Paper>

      {/* Edit Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {formStatus}
          <IconButton
            edge='end'
            onClick={() => setOpen(false)}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Profile Image Upload */}
          {/* <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            my={2}
          >
            <Avatar
              src={formData.profileImg || '/api/placeholder/100/100'}
              alt='Profile Image'
              sx={{ width: 100, height: 100, mb: 2 }}
            />
            <input
              accept='image/*'
              style={{ display: 'none' }}
              id='profile-image-upload'
              type='file'
              onChange={handleImageChange}
            />
            <label htmlFor='profile-image-upload'>
              <Button
                variant='outlined'
                component='span'
                size='small'
              >
                Change Profile Image
              </Button>
            </label>
          </Box> */}

          <TextField
            label='Name'
            fullWidth
            margin='normal'
            name='name'
            value={formData.name || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.name && !!errors.name}
            helperText={touched.name && errors.name}
          />
          <TextField
            label='Email'
            fullWidth
            margin='normal'
            name='email'
            value={formData.email || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.email && !!errors.email}
            helperText={touched.email && errors.email}
          />
          <TextField
            label='Phone'
            fullWidth
            margin='normal'
            name='phone'
            value={formData.phone || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.phone && !!errors.phone}
            helperText={touched.phone && errors.phone}
          />
          <TextField
            label='Password'
            fullWidth
            margin='normal'
            name='password'
            type='password'
            value={formData.password || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.password && !!errors.password}
            helperText={touched.password && errors.password}
            disabled
          />
          <TextField
            label='Address'
            fullWidth
            margin='normal'
            name='address'
            value={formData.address || ''}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextField
            label='Role'
            fullWidth
            margin='normal'
            name='role'
            value={formData.role || 'user'}
            onChange={handleChange}
            onBlur={handleBlur}
            select
            error={touched.role && !!errors.role}
            helperText={touched.role && errors.role}
            disabled
          >
            <MenuItem value='user'>User</MenuItem>
            <MenuItem value='admin'>Admin</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSave}
            variant='contained'
            className='!bg-amber-500 !text-white'
            style={{ width: '200px' }}
          >
            {formStatus}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={!!viewUser}
        onClose={() => setViewUser(null)}
        maxWidth='sm'
      >
        <DialogTitle>View User</DialogTitle>
        <DialogContent>
          {viewUser && (
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
            >
              <Avatar
                src={
                  viewUser.profileImg || '/api/placeholder/100/100'
                }
                alt={viewUser.name}
                sx={{ width: 100, height: 100, mb: 2 }}
              />
              <div className='space-y-2'>
                <Typography variant='h6'>
                  Name: {viewUser.name}
                </Typography>
                <Typography>Email: {viewUser.email}</Typography>
                <Typography>Phone: {viewUser.phone}</Typography>
                <Typography>Password: {viewUser.password}</Typography>
                <Typography>Address: {viewUser.address}</Typography>
                <Typography>Role: {viewUser.role}</Typography>
                <Typography>
                  Created At:{' '}
                  {new Date(viewUser.createdAt).toLocaleString()}
                </Typography>
              </div>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewUser(null)}
            className='!bg-amber-500 !text-white'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this user?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            color='secondary'
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color='primary'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserMaster;
